import { useState } from "react";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { Download, Mail, Pencil, Plus } from "lucide-react";

import { SendEmailModal } from "components/Modal/SendEmailModal/SendEmailModal";
import { Button } from "components/ui/Button";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { PatientNameLink } from "features/communications/components/PatientNameLink/PatientNameLink";
import { usePatientSickCerts } from "features/communications/hooks/usePatientSickCerts";
import { usePracticeSickCerts } from "features/communications/hooks/usePracticeSickCerts";
import { useDownloadSickCert } from "features/consultations/hooks/useDownloadSickCert";
import { SelectPatientDialog } from "features/patients/components/SelectPatientDialog/SelectPatientDialog";
import { usePatientData } from "features/patients/hooks/usePatientData";
import { useAllStaffMembers } from "features/practice/hooks/useAllStaffMembers";
import { toRows, toTotalRecords } from "utils/paginatedResponse";

import { AddSickCertModal } from "./AddSickCertModal";
import { EditSickCertModal } from "./EditSickCertModal";
import styles from "./SickCertsSection.module.css";

const ROWS_OPTIONS = [10, 20, 50];

const STATUS_PILL = {
  SENT: PILL_COLORS.statusSent,
  DRAFT: PILL_COLORS.reactionUnknown,
  DOWNLOADED: PILL_COLORS.statusPending,
};

const formatDate = (value) => {
  if (!value) return "—";

  try {
    return format(new Date(value), "dd MMM yyyy");
  } catch {
    return "—";
  }
};

const getStaffName = (staffMember) => {
  const firstName = staffMember?.firstName ?? "";
  const lastName = staffMember?.lastName ?? "";

  return `${firstName} ${lastName}`.trim();
};

/*
 * The tenant-wide listing carries only the staff id (unlike the per-patient one, which
 * embeds the staff member), so the practice's staff list — already cached — supplies the
 * name rather than leaving a bare id on screen.
 */
const getAuthorName = (row, staffMembers) => {
  const name =
    getStaffName(row.staffMember) ||
    getStaffName(staffMembers?.find((staff) => staff.id === row.staffId));

  return name || "—";
};

const formatStatus = (status) => {
  if (!status) return status;

  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const truncateReason = (text) => {
  if (!text) return "—";

  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
};

export const SickCertsSection = ({ patientId }) => {
  const tenantVal = localStorage.getItem("kinesin-tenant") ?? "";
  const { data: patient } = usePatientData();
  const { data: staffMembers } = useAllStaffMembers();

  /*
   * With no patient in context this is the practice-wide list on the global
   * Communications page: every sick cert in the tenant, with a Patient column, and each
   * row acting on the patient it belongs to rather than on one in context.
   */
  const isPracticeWide = !patientId;

  const [selectedSickCert, setSelectedSickCert] = useState(null);
  const [emailModalSickCert, setEmailModalSickCert] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSelectPatient, setShowSelectPatient] = useState(false);
  /*
   * Practice-wide there is no patient in context, so "Add Sick Cert" asks for one first
   * and the picked patient — not the (absent) prop — is whose chart the cert is written
   * to.
   */
  const [sickCertPatient, setSickCertPatient] = useState(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const patientSickCertsQuery = usePatientSickCerts(patientId, {
    page,
    rows: rowsPerPage,
  });
  const practiceSickCertsQuery = usePracticeSickCerts({
    enabled: isPracticeWide,
  });

  const { data, isLoading, isError, refetch } = isPracticeWide
    ? practiceSickCertsQuery
    : patientSickCertsQuery;
  const { mutate: downloadSickCert } = useDownloadSickCert();

  const certs = toRows(data);
  const totalCount = toTotalRecords(data);

  const addSickCertPatientId = patientId ?? sickCertPatient?.id;

  const handleAddSickCertClick = () => {
    if (isPracticeWide) {
      setShowSelectPatient(true);

      return;
    }

    setShowAddModal(true);
  };

  const handlePatientPicked = (pickedPatient) => {
    setSickCertPatient(pickedPatient);
    setShowSelectPatient(false);
    setShowAddModal(true);
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setSickCertPatient(null);
  };

  const handleOpenSendEmail = (sickCert) => {
    if (!sickCert?.documentId) {
      toast.error("Sick cert PDF isn't ready yet — please save the cert before sending.");

      return;
    }

    setEmailModalSickCert(sickCert);
  };

  const emailDocuments = emailModalSickCert?.documentId
    ? [
        {
          id: emailModalSickCert.documentId,
          name: emailModalSickCert.name || "Sick Certificate",
          documentType: "Sick Certificate",
        },
      ]
    : [];

  const columns = [
    {
      id: "created",
      header: "Created",
      width: "110px",
      render: (row) => formatDate(row.created),
    },
    isPracticeWide && {
      id: "patient",
      header: "Patient",
      width: "120px",
      flexGrow: 1,
      render: (row) => (
        <PatientNameLink patientId={row.patientId} patient={row.patient} />
      ),
    },
    {
      id: "dateFrom",
      header: "Date From",
      width: "110px",
      render: (row) => formatDate(row.dateFrom),
    },
    {
      id: "dateTo",
      header: "Date To",
      width: "110px",
      render: (row) => formatDate(row.dateTo),
    },
    {
      id: "reasonForAbsence",
      header: "Reason",
      width: "140px",
      flexGrow: 1,
      render: (row) => truncateReason(row.reasonForAbsence),
    },
    {
      id: "createdBy",
      header: "Created By",
      // 120px clipped a staff name to "Vaxx DrCertifica…" — the column holds full
      // names, so it needs room for one.
      width: "170px",
      render: (row) => getAuthorName(row, staffMembers),
    },
    {
      id: "status",
      header: "Status",
      width: "130px",
      render: (row) => (
        <Pill {...(STATUS_PILL[row.status] ?? PILL_COLORS.statusNoStatus)}>
          {formatStatus(row.status) || "—"}
        </Pill>
      ),
    },
  ].filter(Boolean);

  const rowActions = [
    {
      label: "Edit",
      icon: Pencil,
      primary: true,
      onClick: (row) => setSelectedSickCert(row),
    },
    {
      label: "Download PDF",
      icon: Download,
      onClick: (row) => {
        // The download endpoint regenerates the PDF on demand from the sick
        // cert record itself, so it takes the sick cert's own id — not
        // documentId (that's a separate patient-document record, and may
        // not exist yet for a cert that's never been downloaded/emailed).
        downloadSickCert({
          sickCertId: row.id,
          tenantVal,
          patientId: patientId ?? row.patientId,
        });
      },
    },
    {
      label: "Send Email",
      icon: Mail,
      onClick: handleOpenSendEmail,
    },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Sick Certs</h2>

        <Button variant="text" endIcon={Plus} onClick={handleAddSickCertClick}>
          Add Sick Cert
        </Button>
      </div>

      {isError && (
        <div className={styles.errorBanner}>
          Could not load sick certificates.
          <button className={styles.retryBtn} onClick={refetch}>Retry</button>
        </div>
      )}

      <DataTable
        columns={columns}
        data={certs}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        rowActions={rowActions}
        onRowClick={(row) => setSelectedSickCert(row)}
        totalCount={totalCount}
        currentPage={page}
        onPageChange={setPage}
        rowsValue={rowsPerPage}
        onRowsChange={(value) => {
          setRowsPerPage(value);
          setPage(1);
        }}
        rowsOptions={ROWS_OPTIONS}
        emptyTitle="No sick certs found"
        emptyText={
          isPracticeWide
            ? "No sick certificates have been added in this practice."
            : "No sick certificates have been added for this patient."
        }
      />

      <SelectPatientDialog
        open={showSelectPatient}
        onClose={() => setShowSelectPatient(false)}
        onSelect={handlePatientPicked}
        title="Add Sick Cert"
        description="Choose the patient this sick certificate belongs to."
      />

      <AddSickCertModal
        open={showAddModal}
        patientId={addSickCertPatientId}
        onClose={handleCloseAddModal}
        onSuccess={refetch}
      />

      <EditSickCertModal
        open={!!selectedSickCert}
        sickCertId={selectedSickCert?.id ?? null}
        patientId={patientId ?? selectedSickCert?.patientId}
        onClose={() => setSelectedSickCert(null)}
        refetchList={refetch}
      />

      <SendEmailModal
        isOpen={!!emailModalSickCert}
        onClose={() => setEmailModalSickCert(null)}
        patient={emailModalSickCert?.patient ?? patient}
        documents={emailDocuments}
      />
    </div>
  );
};
